/** Executing and querying pending function calls; posts to the chats worker's flow-dispatch surface with flow-native snake_case keys. */

import { firstRowOrNull } from '@federa/const/d1-row-reads';
import { CHATS_MFE_API_BASE } from '@federa/const/chats-viewer-routes';
import { FUNCTION_TO_FLOW_MAP } from '@federa/const/function-to-flow-map';
import { HTTP_STATUS } from '@federa/const/http-status';
import { asRecordOrEmpty } from '@federa/const/record-guards';
import { stringOrNull, asStringOrUndefined } from '@federa/const/string-guards';
import { postJsonRouteUserOrGuest } from './route-helpers.ts';
import type { FlowName } from '@federa/types/flows-flow-name.generated';
import type { StandardResult } from '@federa/types/api-response';
import type { ExecuteFunctionCallParams, GetFunctionDetailsParams, GetFunctionDetailsResult } from '@federa/types/api-offer';

function chatsFlowRoute(flowId: string): string {
	return `${CHATS_MFE_API_BASE}/${flowId}`;
}

/** Execute a pending function call. Decline dispatches `function-card-decline`; approve resolves the per-function flow from FUNCTION_TO_FLOW_MAP. */
export async function executeFunctionCall(params: ExecuteFunctionCallParams): Promise<StandardResult> {
	if (params.declined === true) {
		return postJsonRouteUserOrGuest(chatsFlowRoute('function-card-decline' satisfies FlowName), {
			execution_id: params.executionId ?? null,
		});
	}
	const flowName = params.functionName ? FUNCTION_TO_FLOW_MAP[params.functionName] : undefined;
	if (!flowName) {
		// Rejected before any request went out, so there is no body to carry.
		return {
			success: false,
			status: HTTP_STATUS.BAD_REQUEST,
			message: `Unsupported function: ${params.functionName ?? '(missing functionName)'}`,
			raw: undefined,
		};
	}
	return postJsonRouteUserOrGuest(chatsFlowRoute(flowName), {
		execution_id: params.executionId ?? null,
		approved: true,
	});
}

/** Function execution details via the `function-details` flow; the body is the raw executeD1Crud output, so matched rows live under `results`. */
export async function getFunctionDetails(params: GetFunctionDetailsParams): Promise<GetFunctionDetailsResult> {
	const result = await postJsonRouteUserOrGuest(chatsFlowRoute('function-details' satisfies FlowName), {
		execution_id: params.executionId,
	});

	const body = asRecordOrEmpty(result.raw);
	// `firstRowOrNull`, not a `[0] as Record<string, unknown>` cast, and the `isNonEmptyArray` gate it replaces was
	// answering a DIFFERENT question: "the array has an element" is not "the element is a row". A non-record element
	// passed that test and was blessed, after which the four member reads below fed `asStringOrUndefined` /
	// `stringOrNull` -- which each answer `undefined`/`null` for a non-string, so a malformed row rendered as a details
	// pane with every field blank instead of taking the not-found arm below. The `Array.isArray(...) ? ... : undefined`
	// local goes with the cast it existed to make legal: the guard answers `null` for a non-array itself.
	const functionData = firstRowOrNull(body.results ?? body.data);
	if (result.success && functionData !== null) {
		const fnName = functionData.function_name;
		const fnArgs = functionData.input_parameters;
		const fnStatus = functionData.status;
		const fnUserId = functionData.user_id;
		return {
			success: true,
			status: result.status,
			message: result.message,
			raw: result.raw,
			functionTitle: asStringOrUndefined(fnName),
			arguments: asStringOrUndefined(fnArgs),
			functionStatus: asStringOrUndefined(fnStatus),
			functionUserId: stringOrNull(fnUserId),
		};
	}

	const bodyErr = body.error;
	return {
		success: false,
		status: result.status,
		message: typeof bodyErr === 'string' ? bodyErr : 'Function not found. It may have already been processed or the link is invalid.',
		raw: body,
	};
}
